import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  maxWidth: '400px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '24px',
}));

const LogoContainer = styled(Box)({
  textAlign: 'center',
  marginBottom: '30px',
});



const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.disabled,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: theme.palette.text.primary,
  },
}));

const RememberMeContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '30px',
});

const LoginButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '20px',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: theme.palette.common.white,
  },
}));

const RegisterLink = styled(Box)({
  textAlign: 'center',
  marginTop: '20px',
});


const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();

  // Load saved credentials on mount
  useEffect(() => {
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    setRememberMe(savedRememberMe);

    if (savedRememberMe) {
      const savedEmail = localStorage.getItem('email') || '';
      const savedPassword = localStorage.getItem('password') || '';
      setEmail(savedEmail);
      setPassword(savedPassword);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      await login(email, password);

      // Save credentials if "Remember Me" is enabled
      if (rememberMe) {
        localStorage.setItem('email', email);
        localStorage.setItem('password', password);
      } else {
        localStorage.removeItem('email');
        localStorage.removeItem('password');
      }
      localStorage.setItem('rememberMe', rememberMe.toString());

      navigate('/profile');
    } catch (err: any) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginPaper elevation={8}>
        <form onSubmit={handleSubmit}>
          <LogoContainer>
            <Box
              component="img"
              src="/SkimBusterHeaderLogin-21.svg"
              alt="SkimBuster by MICCLO"
              sx={{
                height: '140px',
                objectFit: 'contain',
                filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1) brightness(0.9)' : 'none'
              }}
            />
          </LogoContainer>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '16px' }}>
              {error}
            </Alert>
          )}

          <StyledTextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: muiTheme.palette.text.disabled }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RememberMeContainer>
            <FormControlLabel
              control={
                <Switch
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Typography color={muiTheme.palette.text.secondary}>
                  Remember Me
                </Typography>
              }
            />
          </RememberMeContainer>

          <LoginButton
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Logging in...' : 'Login'}
          </LoginButton>

          <RegisterLink>
            <Typography color={muiTheme.palette.text.secondary}>
              Don't have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/register')}
                sx={{
                  color: muiTheme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Register
              </Link>
            </Typography>
          </RegisterLink>
        </form>
      </LoginPaper>
    </LoginContainer>
  );
};

export default Login;
